import React, { useCallback, useEffect, useState } from "react";
import { Label } from "./Label";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
} from "@mui/material";
import { TextField } from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { CreateLabel } from "./CreateLabel";
import { useSelectedOrganization } from "../../hooks";
import { processesApi, tagsApi } from "../../api";
import { Box } from "@mui/system";
import { LabelData } from "./label_Interface";
import { getTranslation } from 'common';
import { useTranslations } from 'hooks';

interface LabelManagerProps {
  idprocess?: number | undefined;
  open: boolean;
  handleClose: () => void;
  handleOpenCreateLabel: () => void;
  createLabelOpen: boolean;
  handleCreateLabelClose: () => void;
  checked?: LabelData[];
  setChecked?: React.Dispatch<React.SetStateAction<LabelData[]>>;
}

const LabelManager: React.FC<LabelManagerProps> = ({
  idprocess,
  open,
  handleClose,
  handleOpenCreateLabel,
  createLabelOpen,
  handleCreateLabelClose,
  checked,
  setChecked
}) => {
  const selectedOrganization = useSelectedOrganization();
  const organizationId = selectedOrganization?.id;
  const [labelList, setLabelList] = useState<LabelData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [defaultCheck, setDefaultCheck] = useState<number[]>([]);
  const [checking, setChecking] = useState<number[]>([]);

  const handleAdd = (label: LabelData) => {
    fetchLabels();
  };

  const fetchLabels = useCallback(async () => {
    try {
      const promises = [tagsApi.get(`/${organizationId}/tags`)];
      if (idprocess) {
        promises.push(processesApi.get(`/${idprocess}/tags`));
      }
      const [response, fromProcess] = await Promise.all(promises);
      if (fromProcess) {
        setDefaultCheck(
          fromProcess.data.processtags.map(
            (tag: { id: number; name: string; color: string }) => tag.id
          )
        );
      }

      // Accessing 'tag' property of response data
      if (Array.isArray(response.data.tag)) {
        const labels = response.data.tag.map(
          (tag: { id: number; name: string; color: string }) => ({
            id: tag.id,
            name: tag.name,
            css: tag.color,
          })
        );
        setLabelList(labels);
      } else {
        console.error(
          "Expected response.data.tag to be an array but received",
          response.data.tag
        );
      }
    } catch (err) {
      console.error(`Failed to fetch labels: ${err}`);
    } finally {
      setLoading(false);
    }
  }, [idprocess, organizationId]);

  const translations = useTranslations();

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);


  const handleCheckLabel = async (label: LabelData, newCheck: boolean) => {
    try {
      setChecking((_) => [..._, label.id]);
      if (newCheck) {
        if (idprocess) {
          await tagsApi.post(
            `/${organizationId}/processes/${idprocess}/tags/${label.id}`
          );
        } else {
          setChecked && setChecked((_) => [..._, label]);
        }
      } else {
        if (idprocess) {
          await tagsApi.delete(
            `/${organizationId}/processes/${idprocess}/tags/${label.id}`
          );
        } else {
          setChecked && setChecked((_) => {
            const c_ = [..._];
            const c_idx = c_.findIndex((__) => __.id === label.id);
            c_.splice(c_idx, 1);
            return c_;
          });
        }
      }
    } catch {
    } finally {
      setChecking((_) => {
        const c_ = [..._];
        const c_idx = c_.findIndex((__) => __ === label.id);
        c_.splice(c_idx, 1);
        return c_;
      });
    }
  };

  const deleteLabel = async (labelId: number) => {
    try {
      await tagsApi.delete(`/${organizationId}/tags/${labelId}`);
      fetchLabels();
    } catch (err) {
      console.error(`Failed to delete label: ${err}`);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "center",
          fontWeight: "normal",
          position: "relative",
        }}
      >
        {getTranslation(translations, "generic.labels.caption")}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 0,
          }}
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <Divider variant="middle" />
      <Stack direction="column" spacing={1} sx={{ p: 2 }}>
        <TextField
          value={searchValue}
          onChange={({ target }) => setSearchValue(target.value)}
          placeholder={getTranslation(translations, "generic.searchlabels.caption")}
          variant="outlined"
          size="small"
          sx={{ mb: 2 }}
        />
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              maxHeight: "200px",
              overflowY: "auto",
              // pt: 2,
            }}
          >
            {Array.isArray(labelList) &&
              labelList
                .filter((x) =>
                  x.name.toLowerCase().includes(searchValue.toLowerCase())
                )
                .map((label: LabelData) => (
                  <Box
                    key={label.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      // marginBottom: "10px",
                    }}
                  >
                    {idprocess ? (
                      <Checkbox
                        defaultChecked={defaultCheck.includes(label.id)}
                        disabled={checking.includes(label.id)}
                        onChange={(_e, newCheck) =>
                          handleCheckLabel(label, newCheck)
                        }
                        sx={{ color: (_) => _.palette.grey[600] }}
                      />
                    ) : (
                      <Checkbox
                        checked={checked && checked.findIndex(_ => _.id === label.id) >= 0}
                        onChange={(_e, newCheck) =>
                          handleCheckLabel(label, newCheck)
                        }
                        sx={{ color: (_) => _.palette.grey[600] }}
                      />
                    )}
                    <Box sx={{ textAlign: "center" }}>
                      <Label
                        color={label.css}
                        name={label.name}
                        onRemove={() => deleteLabel(label.id)}
                      />
                    </Box>
                  </Box>
                ))}
          </Box>
        )}
        <Box sx={{ height: "40px" }} />
        <Button
          size="small"
          variant="semitransparent"
          component="label"
          onClick={handleOpenCreateLabel}
        >
          {getTranslation(translations, "generic.createnewlabel.caption")}
        </Button>
        {createLabelOpen && (
          <CreateLabel
            open={createLabelOpen}
            onAdd={handleAdd}
            onClose={handleCreateLabelClose}
          />
        )}
      </Stack>
    </Dialog>
  );
};

export default LabelManager;
